import React, { useState, useEffect } from 'react';
import { useStatsigClient } from '@statsig/react-bindings';
import Banner from './Banner';
import useSupabase from '../supabase/useSupabase';
import Turnstile from './Turnstile';
import { isValidEmailFormat, isDisposableEmail, checkDomainExists } from '../utils/emailValidator';
import useUnifiedAnalytics from '../analytics/useUnifiedAnalytics';
import { EVENTS } from '../analytics/events';

const ConfirmScreen = ({ contestant, onBack, onSubmit, pollConfig, siteConfig, onNavigateToTerms, onNavigateToPrivacy }) => {
  const { client } = useStatsigClient();
  const { submitVoteForContestant } = useSupabase();
  const analytics = useUnifiedAnalytics();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [turnstileError, setTurnstileError] = useState(null);
  const [bypassTurnstile, setBypassTurnstile] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  
  // Detect if we're running on localhost or Docker environment
  const isLocalhost = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1' ||
                      window.location.hostname.includes('192.168.') ||
                      window.location.hostname.includes('172.') ||
                      window.location.hostname.includes('10.');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    agreeToTerms: false
  });
  
  const [errors, setErrors] = useState({});
  const [hasInteractedWithForm, setHasInteractedWithForm] = useState(false);
  
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    
    // Clear error when field is being edited
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  
  const handleFocus = (e) => {
    // Clear error when field is focused
    const { name } = e.target;
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
    
    // Track form interaction once per session when user focuses on a form field
    if (!hasInteractedWithForm) {
      analytics.trackEvent(EVENTS.FORM_INTERACTION, name, {
        contestant_id: contestant?.id,
        field_name: name
      });
      setHasInteractedWithForm(true);
    }
  };
  
  // Basic form validation (synchronous)
  const validateFormBasics = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    // Last name is now optional, so no validation needed
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!isValidEmailFormat(formData.email)) {
      newErrors.email = 'Email format is invalid';
    } else if (isDisposableEmail(formData.email)) {
      newErrors.email = 'Disposable email addresses are not allowed';
    }
    
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }
    
    // Only require Turnstile token if not on localhost or not bypassed
    if (!isLocalhost && !turnstileToken) {
      newErrors.turnstile = 'Please complete the verification';
    } else if (isLocalhost && !turnstileToken && !bypassTurnstile) {
      newErrors.turnstile = 'Please complete the verification or use the bypass option';
    }
    
    return newErrors;
  };
  
  // Advanced email validation with domain check (asynchronous)
  const validateEmailDomain = async (email) => {
    try {
      setIsCheckingEmail(true);
      const domain = email.split('@')[1];
      if (!domain) return false;
      
      console.log(`Checking MX records for domain: ${domain}`);
      
      // Log email validation attempt
      client?.logEvent("email_validation_attempt", {
        domain: domain,
        contestant_id: contestant.id,
        timestamp: new Date().toISOString()
      });
      
      const domainExists = await checkDomainExists(domain);
      
      if (!domainExists) {
        setErrors(prev => ({
          ...prev,
          email: 'This email domain appears to be invalid'
        }));
        
        // Log invalid domain
        client?.logEvent("email_validation_failed", {
          reason: "invalid_mx_records",
          domain: domain,
          contestant_id: contestant.id,
          timestamp: new Date().toISOString()
        });
        
        return false;
      }
      
      // Log successful validation
      client?.logEvent("email_validation_success", {
        domain: domain,
        contestant_id: contestant.id,
        timestamp: new Date().toISOString()
      });
      
      return true;
    } catch (error) {
      console.error('Error validating email domain:', error);
      
      // Log validation error
      client?.logEvent("email_validation_error", {
        error_message: error.message,
        domain: email.split('@')[1] || 'unknown',
        contestant_id: contestant.id,
        timestamp: new Date().toISOString()
      });
      
      // Don't block submission on MX check failure
      return true;
    } finally {
      setIsCheckingEmail(false);
    }
  };
  
  // Complete form validation
  const validateForm = () => {
    const newErrors = validateFormBasics();
    
    // Display validation errors
    setErrors(newErrors);
    console.log('Form validation errors:', newErrors);
    
    // Log validation errors if any
    if (Object.keys(newErrors).length > 0) {
      client?.logEvent("form_validation_failed", {
        error_fields: Object.keys(newErrors),
        contestant_id: contestant.id,
        timestamp: new Date().toISOString(),
        email_domain: formData.email ? formData.email.split('@')[1] || 'unknown' : 'empty'
      });
    }
    
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submission attempt with data:', { 
      ...formData, 
      turnstileToken: turnstileToken ? `${turnstileToken.substring(0, 10)}...` : 'none',
      bypassEnabled: bypassTurnstile
    });
    
    // Force validation to show any errors
    const isValid = validateForm();
    console.log('Form validation result:', isValid ? 'Valid' : 'Invalid');
    
    if (isValid) {
      // Additional email domain validation
      setIsCheckingEmail(true);
      
      // Log form submission attempt with domain check
      client?.logEvent("form_submission_with_domain_check", {
        contestant_id: contestant.id,
        email_domain: formData.email.split('@')[1] || 'unknown',
        timestamp: new Date().toISOString()
      });
      
      const emailDomainValid = await validateEmailDomain(formData.email);
      
      if (!emailDomainValid) {
        console.log('Email domain validation failed');
        return;
      }
      
      setIsSubmitting(true);
      setSubmitError(null);
      
      try {
        // Prepare vote data for submission
        const voteData = {
          contestantId: contestant.id,
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          turnstileToken: turnstileToken // Include the Turnstile token
        };
        
        // Submit the vote
        const result = await submitVoteForContestant(voteData);
        
        if (result.success) {
          // Clear form and show success message
          console.log('Vote submitted successfully!');
          
          // Log successful vote submission with Statsig
          client?.logEvent("vote_submitted", "success", {
            character_id: contestant.id,
            character_name: contestant.display_name,
            validation_method: isLocalhost && bypassTurnstile ? 'localhost_bypass' : 'turnstile',
            turnstile_used: !bypassTurnstile,
            submission_time: new Date().toISOString()
          });
          
          // Track vote submission with unified analytics service
          analytics.trackEvent(EVENTS.VOTE_SUBMITTED, contestant.id, { 
            has_email: formData.email ? true : false,
            character_name: contestant.display_name,
            validation_method: isLocalhost && bypassTurnstile ? 'localhost_bypass' : 'turnstile',
            submission_time: new Date().toISOString()
          });
          
          // Pass the data to parent component
          onSubmit({
            ...formData,
            contestant,
            turnstileToken: bypassTurnstile ? 'localhost_bypass' : turnstileToken
          });
        } else {
          // Handle submission error
          setSubmitError(result.error || 'Failed to submit your vote. Please try again.');
          
          // Log failed submission attempt due to API error
          client?.logEvent("vote_submission_failed", "api_error", {
            character_id: contestant.id,
            character_name: contestant.display_name,
            error_message: result.error,
            attempt_time: new Date().toISOString()
          });
        }
      } catch (error) {
        console.error('Error submitting vote:', error);
        setSubmitError('An unexpected error occurred. Please try again.');
        
        // Log failed submission attempt due to exception
        client?.logEvent("vote_submission_failed", "exception", {
          character_id: contestant.id,
          character_name: contestant.display_name,
          error_message: error.message,
          attempt_time: new Date().toISOString()
        });
      } finally {
        setIsSubmitting(false);
      }
    } else {
      // Log failed submission attempt due to validation errors
      client?.logEvent("vote_submission_failed", "validation_error", {
        character_id: contestant.id,
        character_name: contestant.display_name,
        error_fields: Object.keys(errors),
        attempt_time: new Date().toISOString()
      });
    }
  };
  
  return (
    <div className="min-h-screen bg-toon-yellow p-4">
      <Banner text={siteConfig?.banner} />
      <div className="max-w-md mx-auto">
        <div className="text-center mb-6">
          <img 
            src="/assets/Toon_House_Logo.svg" 
            alt="Toon House Logo" 
            className="h-24 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold mb-2">✅ CONFIRM YOUR ENTRY</h1>
        </div>
        
        {/* Selected Contestant */}
        <div className="rounded-2xl shadow-lg overflow-hidden mb-6">
          <div className="relative">
            {/* Character Image */}
            <div className="w-full overflow-hidden flex justify-center items-center" style={{ minHeight: '200px' }}>
              <img 
                src={contestant.image_url} 
                alt={contestant.display_name} 
                className="w-full h-auto object-contain" 
                style={{ maxHeight: '300px' }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/300?text=' + contestant.display_name;
                }}
              />
            </div>
            
            {/* Selection indicator */}
            <div className="absolute top-3 right-3 bg-green-500 text-white rounded-full p-1">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          
          {/* Text area at bottom */}
          <div className="bg-gray-900 text-white p-4">
            <h3 className="text-xl font-bold uppercase">{contestant.display_name}</h3>
            <p className="text-sm text-gray-300">You selected this contestant</p>
          </div>
        </div>
        
        {/* Voter Information Form */}
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md">
          <div className="mb-4">
            <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
              First Name
            </label>
            <input
              type="text"
              id="firstName"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              onFocus={handleFocus}
              className={`w-full px-3 py-2 border rounded-md ${
                errors.firstName ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.firstName && (
              <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
            )}
          </div>
          
          <div className="mb-4">
            <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
              Last Name (Optional)
            </label>
            <input
              type="text"
              id="lastName"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              onFocus={handleFocus}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
            {errors.lastName && (
              <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
            )}
          </div>
          
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onFocus={handleFocus}
              className={`w-full px-3 py-2 border rounded-md ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
              disabled={isCheckingEmail}
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>
          
          <div className="mb-6">
            <div className="flex items-start">
              <input
                type="checkbox"
                id="agreeToTerms"
                name="agreeToTerms"
                checked={formData.agreeToTerms}
                onChange={handleChange}
                className={`mt-1 ${errors.agreeToTerms ? 'border-red-500' : ''}`}
              />
              <label htmlFor="agreeToTerms" className="ml-2 block text-sm text-gray-700">
                I have read and agree to the <a href="#" onClick={(e) => {
                  e.preventDefault();
                  onNavigateToTerms && onNavigateToTerms();
                }} className="text-blue-600 hover:text-blue-800 underline">Terms and Conditions</a>. I also acknowledge and consent to the collection and use of my information as described in the <a href="#" onClick={(e) => {
                  e.preventDefault();
                  onNavigateToPrivacy && onNavigateToPrivacy();
                }} className="text-blue-600 hover:text-blue-800 underline">Privacy Policy</a>.
              </label>
            </div>
            {errors.agreeToTerms && (
              <p className="text-red-500 text-xs mt-1">{errors.agreeToTerms}</p>
            )}
          </div>
          
          {/* Cloudflare Turnstile */}
          <div className="mb-4">
            <Turnstile 
              siteKey={isLocalhost ? '1x00000000000000000000AA' : '0x4AAAAAABmZDQI0L3UKPgGi'}
              onVerify={(token) => {
                setTurnstileToken(token);
                setTurnstileError(null);
                console.log('Turnstile verification successful:', token.substring(0, 10) + '...');
              }}
              onError={(error) => {
                setTurnstileError(error);
                setTurnstileToken(null);
                console.error('Turnstile verification failed:', error);
              }}
            />
            {errors.turnstile && (
              <p className="text-red-500 text-xs mt-1">{errors.turnstile}</p>
            )}
            {turnstileError && (
              <p className="text-red-500 text-xs mt-1">{turnstileError}</p>
            )}
            
            {/* Local development bypass option */}
            {isLocalhost && (
              <div className="mt-2">
                <label className="flex items-center text-sm text-gray-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={bypassTurnstile}
                    onChange={(e) => setBypassTurnstile(e.target.checked)}
                    className="mr-2"
                  />
                  Bypass verification (localhost only)
                </label>
              </div>
            )}
          </div>
          
          {submitError && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {submitError}
            </div>
          )}
          
          <button 
            type="submit" 
            className="w-full bg-green-500 text-white py-3 px-4 rounded-md font-semibold hover:bg-green-600 transition-colors"
            disabled={isSubmitting || isCheckingEmail || (!turnstileToken && !bypassTurnstile)}
          >
            {isSubmitting ? 'SUBMITTING...' : 
             isCheckingEmail ? 'VALIDATING EMAIL...' : 
             'SUBMIT MY VOTE'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ConfirmScreen;
